<script setup lang="ts">
import { computed, onMounted, watch } from 'vue'
import { storeToRefs } from 'pinia'
import ResourceCard from '@/resources/ResourceCard.vue'
import { assessmentDetails, firstAlignedGoal, sortByHighestGradeFirst, subjectsOf } from '@/browse/assessments/assessment_results'
import type { AssessmentHit } from '@/browse/assessments/assessment_results'
import { assessmentSubjectsForProfile } from '@/browse/assessments/assessment_taxonomy'
import {
  sendAlgoliaClickedAssessmentResultEvent,
  sendAlgoliaConvertedAssessmentResultEvent,
  sendAlgoliaViewedAssessmentResultsEvents
} from '@/search/algolia_search'
import type { AssessmentResultInteraction } from '@/search/algolia_search'
import { useAgenticSearchStore } from '@/search/agentic_search.store'
import { useUserStore } from '@/authentication/user.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
import { EVENT_NAMES } from '@/tracking/analytics/event_names'

const ALGOLIA_EVENT_NAMES = {
  CLICKED_DOWNLOAD: 'Clicked Browse Assessments Result Download Dropdown',
  OPENED_DOWNLOAD_LINK: 'Opened Browse Assessments Result Download Link',
  SHOWED_PREVIEW: 'Clicked Browse Assessments Result Preview',
  CLICKED_SAVE: 'Clicked Browse Assessments Result Save'
}

const props = withDefaults(defineProps<{
  assessments: AssessmentHit[]
  algoliaQueryId?: string
  algoliaIndex?: string
  isFromWizard?: boolean
}>(), { algoliaQueryId: '', algoliaIndex: '', isFromWizard: false })

interface PositionedAssessment extends AssessmentHit {
  algoliaPosition: number
}

const { userId } = storeToRefs(useUserStore())
const agenticSearch = useAgenticSearchStore()
const analytics = getAnalyticsTracker().getChildContext({ from_browse_assessments: true })

const profileSubjects = computed(() => assessmentSubjectsForProfile())
const isAlgoliaResultList = computed(() => !props.isFromWizard)
const detailsLineHeightClass = computed(() => (props.isFromWizard ? 'leading-4' : ''))

// Algolia counts positions from 1, in the order it returned the results.
const positionedAssessments = computed<PositionedAssessment[]>(() =>
  props.assessments.map((assessment, index) => ({ ...assessment, algoliaPosition: index + 1 })))

// The wizard's results arrive ranked by relevance, so only Algolia's are reordered.
const displayedAssessments = computed(() => {
  if (props.isFromWizard) {
    return positionedAssessments.value
  }
  return sortByHighestGradeFirst(positionedAssessments.value)
})

function typeHeaderFor(assessment: AssessmentHit): string {
  if (props.isFromWizard) {
    return ''
  }
  return `${assessment.type} Assessment`
}

function interactionWith(assessment: PositionedAssessment, eventName: string): AssessmentResultInteraction {
  return {
    eventName,
    userToken: userId.value,
    index: props.algoliaIndex,
    queryID: props.algoliaQueryId,
    objectID: assessment.objectID,
    position: assessment.algoliaPosition
  }
}

function recordClick(assessment: PositionedAssessment, eventName: string) {
  if (!isAlgoliaResultList.value) {
    return
  }
  sendAlgoliaClickedAssessmentResultEvent(interactionWith(assessment, eventName))
}

function recordClickAndConversion(assessment: PositionedAssessment, eventName: string) {
  if (!isAlgoliaResultList.value) {
    return
  }
  const interaction = interactionWith(assessment, eventName)
  sendAlgoliaClickedAssessmentResultEvent(interaction)
  sendAlgoliaConvertedAssessmentResultEvent(interaction)
}

function returnedResultsEventData() {
  const hasResults = props.assessments.length > 0
  const eventData = {
    browse_assessment_num_results: props.assessments.length,
    algolia_query_id: hasResults ? props.algoliaQueryId : ''
  }
  if (!props.isFromWizard) {
    return eventData
  }
  return { ...eventData, agentic_search_id: agenticSearch.latestSearch.searchId, selected_grade: agenticSearch.latestSearch.searchGrade }
}

// Recorded when the list mounts with its first results, and again with whatever it shows just
// before each filter change, which is when the page calls this; reporting relies on that timing.
function recordReturnedResults() {
  analytics.sendEvent(EVENT_NAMES.BROWSE_ASSESSMENTS.RETURNED_RESULTS, returnedResultsEventData())
}

function recordViewedResults() {
  if (!isAlgoliaResultList.value) {
    return
  }
  sendAlgoliaViewedAssessmentResultsEvents({
    userToken: userId.value,
    index: props.algoliaIndex,
    objectIDs: props.assessments.map((assessment) => assessment.objectID)
  })
}

onMounted(recordReturnedResults)
// Each Algolia search has its own query ID, so this reports every new set of results viewed once.
watch(() => props.algoliaQueryId, recordViewedResults, { immediate: true })

defineExpose({ recordReturnedResults })
</script>

<template>
  <ol class="list-none space-y-1 p-0">
    <li v-for="assessment in displayedAssessments" :key="assessment.uuid" class="py-1" data-testid="browse-assessment-result">
      <ResourceCard
        :uuid="assessment.uuid"
        :title="assessment.title"
        :description="assessment.description"
        :type="assessment.type"
        :thumbnail_url="assessment.thumbnail_url"
        :grades="assessment.grades ?? []"
        :screenshots="assessment.screenshots ?? []"
        :subjects="subjectsOf(assessment)"
        :download_links="assessment.download_links"
        :google_drive_links="assessment.google_drive_links"
        :aligned-goal="firstAlignedGoal(assessment)"
        :show-resource-type="false"
        :type-header="typeHeaderFor(assessment)"
        :set_number="assessment.set_number"
        :rationale="assessment.rationale"
        :relevance-score="assessment.relevance_score"
        :heading-level="3"
        @resource-download-clicked="recordClick(assessment, ALGOLIA_EVENT_NAMES.CLICKED_DOWNLOAD)"
        @resource-download-opened="recordClickAndConversion(assessment, ALGOLIA_EVENT_NAMES.OPENED_DOWNLOAD_LINK)"
        @resource-preview-shown="recordClickAndConversion(assessment, ALGOLIA_EVENT_NAMES.SHOWED_PREVIEW)"
        @resource-saved="recordClickAndConversion(assessment, ALGOLIA_EVENT_NAMES.CLICKED_SAVE)"
      >
        <template #resource-details>
          <p class="mb-4 pt-2 text-md text-gray-700" :class="detailsLineHeightClass">
            {{ assessmentDetails(assessment, profileSubjects, isFromWizard) }}
          </p>
        </template>
      </ResourceCard>
    </li>
  </ol>
</template>
